import type {
  GstThresholdOutput,
  RideshareGstOutput,
  SetAsideOutput,
} from "./types.js";

// Plain-English result copy for the side-income surface. Lives in the rules
// package so every channel (Expo result screen, "my results" list, PDF report)
// renders identical wording from one source — which matters for REGULATION, not
// just DRY. Every string here is INFORMATION, not advice (validation §3):
//
//   • The set-aside % is framed as an ESTIMATE ("set aside at least X%"), with
//     "your actual bill depends on total income and deductions you claim".
//   • The two auto-enrolment flags STATE THE ATO RULE and what it means for the
//     user's figures — never "you should register" / "you should start
//     instalments" (advice-drift is the kill-threat the panels police).
//   • The GST price impact states BOTH bases explicitly (+10% on your ex-GST
//     price; the GST you remit is 1/11 ≈ 9.1% of the new price) so the voluntary
//     trade-off is never misstated.
//   • The voluntary trade-off never resolves to "worth it / not worth it".
//
// Pure string-builders, unit-tested.

/** Whole-dollar AUD with thousands separators. */
export function formatAud(amount: number): string {
  return `A$${Math.round(amount).toLocaleString("en-AU")}`;
}

/** A fraction rendered as a whole-or-one-dp percent (e.g. 0.35 → "35%"). */
export function formatPct(fraction: number): string {
  return `${+(fraction * 100).toFixed(2)}%`;
}

/** Mandatory across every side-income surface (validation §3.1). */
export const DISCLAIMER = "Educational tool. Not financial advice.";

// ---------------------------------------------------------------------------
// setAside copy
// ---------------------------------------------------------------------------

export function setAsideHeadline(out: SetAsideOutput): string {
  if (out.abnIncome === 0 || out.setAsidePct === 0) {
    return "Nothing to set aside on the figures you entered";
  }
  return `Set aside at least ${formatPct(out.setAsidePct)} of each invoice for tax`;
}

/** States the extra tax as a stacked figure — never as a saving or a target. */
export function extraTaxBullet(out: SetAsideOutput): string {
  if (out.extraTax === 0) {
    return `On the figures you entered, your side income adds no extra tax this year (your combined income stays below the point where tax applies).`;
  }
  return (
    `Stacked on your ${formatAud(out.salary)} salary, your ${formatAud(out.abnIncome)} of ` +
    `net side income is estimated to add ${formatAud(out.extraTax)} in tax — each extra dollar ` +
    `is taxed at your combined marginal rate, not in isolation. This is an estimate; your actual ` +
    `bill depends on your total income and the deductions you claim.`
  );
}

/** The set-aside %, framed conservatively as a guide, not a guarantee. */
export function setAsideBullet(out: SetAsideOutput): string {
  if (out.setAsidePct === 0) {
    return `There is nothing to set aside on these figures.`;
  }
  return (
    `Setting aside at least ${formatPct(out.setAsidePct)} of each invoice covers the estimated tax ` +
    `on your side income (rounded up so you are not left short). Your actual bill depends on your ` +
    `total income and deductions.`
  );
}

/** PAYG-instalment flag — STATES THE ATO RULE, never "you should". */
export function paygInstalmentBullet(out: SetAsideOutput): string {
  const f = out.flags.paygInstalments;
  if (f.likely) {
    const taxBasis = f.usedPriorYearNoa
      ? "the tax on your latest Notice of Assessment"
      : "your estimated tax for this year";
    return (
      `ATO rule: the ATO automatically enters an individual into PAYG instalments when instalment ` +
      `income is A$4,000 or more AND tax on the latest Notice of Assessment is A$1,000 or more. On ` +
      `your figures both legs are met (${taxBasis}), so expect the ATO to enrol you in PAYG ` +
      `instalments next year — quarterly pre-payments toward this tax, not an extra charge.`
    );
  }
  return (
    `ATO rule: the ATO enters you into PAYG instalments only when instalment income is A$4,000 or ` +
    `more AND tax on your latest Notice of Assessment is A$1,000 or more. On your figures that ` +
    `threshold is not met.`
  );
}

/** GST trip-wire flag from the set-aside surface — cited rule, points to the
 * deep GST check rather than recommending registration. */
export function gstFlagBullet(out: SetAsideOutput): string {
  const g = out.flags.gstRegistration;
  if (g.likely) {
    return (
      `ATO rule: you must register for GST once your GST turnover reaches ${formatAud(g.threshold)} ` +
      `(current or projected over 12 months). Your expected side income reaches that figure — check ` +
      `the GST registration test for the full rolling-12-month calculation and the 21-day deadline.`
    );
  }
  return (
    `ATO rule: GST registration is required once GST turnover reaches ${formatAud(g.threshold)} over ` +
    `a rolling 12 months. Your expected side income is below that figure.`
  );
}

export function setAsideBullets(out: SetAsideOutput): string[] {
  return [
    extraTaxBullet(out),
    setAsideBullet(out),
    paygInstalmentBullet(out),
    gstFlagBullet(out),
  ];
}

// ---------------------------------------------------------------------------
// gstThreshold copy
// ---------------------------------------------------------------------------

export function gstThresholdHeadline(out: GstThresholdOutput): string {
  return out.registrationRequired
    ? `Based on your figures, your GST turnover meets the ${formatAud(out.threshold)} registration test`
    : `Based on your figures, your GST turnover is below the ${formatAud(out.threshold)} registration test`;
}

/** The verdict, framed "based on the figures you entered" — never a legal
 * determination — and stating the simplifying assumptions. */
export function gstThresholdBullet(out: GstThresholdOutput): string {
  const legs: string[] = [];
  if (out.currentMeets) {
    legs.push(`current turnover ${formatAud(out.currentTurnover)} (past 12 months)`);
  }
  if (out.projectedMeets) {
    legs.push(`projected turnover ${formatAud(out.projectedTurnover)} (next 12 months)`);
  }
  if (out.registrationRequired) {
    return (
      `ATO rule: you must register for GST when GST turnover is ${formatAud(out.threshold)} or more, ` +
      `current OR projected over a rolling 12 months. On the figures you entered (${legs.join(" and ")}) ` +
      `the test is met. You must register within ${out.registrationDeadlineDays} days of meeting it. ` +
      `This assumes your turnover is gross business income with no GST-free or input-taxed supplies and ` +
      `no business grouping — the ATO makes the final determination.`
    );
  }
  return (
    `ATO rule: GST registration is required when GST turnover is ${formatAud(out.threshold)} or more, ` +
    `current OR projected over a rolling 12 months. On your figures (current ${formatAud(out.currentTurnover)}, ` +
    `projected ${formatAud(out.projectedTurnover)}) neither leg meets the test, so registration is not required yet.`
  );
}

/** The retrospective-GST warning — a cited factual risk, no dollar figure. */
export function retrospectiveBullet(out: GstThresholdOutput): string | null {
  if (!out.retrospectiveRisk) return null;
  return (
    `ATO rule: if you were required to register and did not, you may have to pay GST on sales made ` +
    `since the date you were required to register — even if you did not include GST in the price — ` +
    `plus possible penalties and interest. Your current (past 12-month) turnover already meets the test.`
  );
}

/** The neutral voluntary trade-off — TWO bases stated, NO resolving row. */
export function voluntaryTradeOffBullet(out: GstThresholdOutput): string {
  const v = out.voluntary;
  return (
    `Voluntary registration is your choice. If you register: you can claim input tax credits on ` +
    `business purchases (estimated ${formatAud(v.inputCreditEstimate)} on the purchases you entered), ` +
    `but you add ${formatPct(v.priceAddOnPct)} to your ex-GST price (the GST you remit is 1/11 ≈ ` +
    `${formatPct(v.inclusivePriceSharePct)} of the new price). Whether that is worthwhile depends on ` +
    `your customer mix — the choice is yours.`
  );
}

export function gstThresholdBullets(out: GstThresholdOutput): string[] {
  const retro = retrospectiveBullet(out);
  return [
    gstThresholdBullet(out),
    ...(retro ? [retro] : []),
    voluntaryTradeOffBullet(out),
  ];
}

// ---------------------------------------------------------------------------
// rideshareGst copy
// ---------------------------------------------------------------------------

export function rideshareGstHeadline(out: RideshareGstOutput): string {
  if (out.reason === "ride-sourcing-from-first-dollar") {
    return "Ride-sourcing means GST registration from your first dollar";
  }
  if (out.reason === "turnover-threshold-met") {
    return `Your turnover meets the ${formatAud(out.thresholdIfNoRideSourcing)} GST registration test`;
  }
  return `Below the ${formatAud(out.thresholdIfNoRideSourcing)} GST registration test on your figures`;
}

/** The bright-line registration verdict, cited to the ATO ride-sourcing rule. */
export function rideshareGstBullet(out: RideshareGstOutput): string {
  if (out.reason === "ride-sourcing-from-first-dollar") {
    return (
      `ATO rule: ride-sourcing is taxi travel for GST purposes, so the ${formatAud(out.thresholdIfNoRideSourcing)} ` +
      `turnover threshold does not apply — you must be registered for GST before your first trip and ` +
      `charge GST on every dollar, regardless of how much you earn.`
    );
  }
  if (out.reason === "turnover-threshold-met") {
    return (
      `ATO rule: food delivery and other gig work are not taxi travel, so the standard ${formatAud(out.thresholdIfNoRideSourcing)} ` +
      `GST turnover test applies. On your figures that test is met, so registration is required.`
    );
  }
  return (
    `ATO rule: food delivery and other gig work are not taxi travel, so the standard ${formatAud(out.thresholdIfNoRideSourcing)} ` +
    `GST turnover test applies. On your figures you are below it.`
  );
}

/** The mixed-activity note: any ride-sourcing forces GST across the whole ABN. */
export function mixedActivityBullet(out: RideshareGstOutput): string | null {
  if (!out.mixedActivityForcesGst) return null;
  return (
    `ATO rule: because you do ride-sourcing, GST registration applies to your whole ABN — including ` +
    `your food-delivery and other gig income, which you then apportion across your activities.`
  );
}

/** The SERR note — a cited current fact, never a "you'll get caught" scare. */
export function serrBullet(out: RideshareGstOutput): string | null {
  if (!out.serrReportingApplies) return null;
  return (
    `ATO fact: under the Sharing Economy Reporting Regime, your platform reports your income to the ATO ` +
    `twice a year (by 31 January for July–December, and 31 July for January–June).`
  );
}

export function rideshareGstBullets(out: RideshareGstOutput): string[] {
  const mixed = mixedActivityBullet(out);
  const serr = serrBullet(out);
  return [
    rideshareGstBullet(out),
    ...(mixed ? [mixed] : []),
    ...(serr ? [serr] : []),
  ];
}
